import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import atto1 from './Asset/atto-1.webp';
import atto2 from './Asset/atto-2.webp';
import atto3 from './Asset/atto-3.webp';
import atto4 from './Asset/atto-4.webp';
import atto5 from './Asset/atto-5.webp';
import atto6 from './Asset/atto-6.webp';
import atto7 from './Asset/atto-7.webp';
import atto8 from './Asset/atto-8.webp';
import atto9 from './Asset/atto-9.webp';
import atto10 from './Asset/atto-10.webp';
import brochure from './Asset/ATTO3-BROCHURE.pdf';
import PageSeo, {
  breadcrumbJsonLd,
  keywordSets,
  productJsonLd,
} from './PageSeo';

function JelajahiAtto() {
  const sections = useRef([]);
  const [visibleSections, setVisibleSections] = useState({});

  const images = [
    {
      src: atto1,
      title: 'BYD ATTO 3',
      specs: [
        { value: '7.3 detik', label: '0-100 km/jam' },
        { value: '480km', label: 'Jarak (NEDC)' },
        { value: '16 kWh/100km', label: 'Konsumsi Energi (Kombinasi WLTP)' },
      ],
    },
    {
      src: atto2,
      title: 'Warna Eksterior Baru',
      description: `BYD Atto 3 dalam balutan warna Cosmos Black dengan trim D-pillar hitam terbaru dan trim jendela hitam, memancarkan aura sporty yang super keren dan trendi.`,
    },
    {
      src: atto3,
      title: 'Roda Keren',
      description: `BYD ATTO 3 ban yang berukuran 18 inci sebagai standar, dengan aksesori merek yang mutakhirkan.`,
    },
    {
      src: atto4,
      title: 'Tim Desainer Kelas Internasional',
      description: `Dipimpin oleh Kepala Desainer Otomotif Wolfgang Egger, tim desain kelas dunia mengintegrasikan inovasi dengan kualitas dan keandalan.`,
    },
    {
      src: atto5,
      title: 'Lampu Depan LED Kombinasi Bulu Sayap Naga Kristal',
      description: `Desain lampu siang hari terlihat elegan dan selaras dengan elemen kumis naga, memberikan pengalaman berkendara di malam hari.`,
    },
    {
      src: atto6,
      title: 'Interior Berirama',
      description: `BYD ATTO 3 memperkenalkan interior baru berwarna biru tua dan hitam, mencerminkan sikap muda.`,
    },
    {
      src: atto7,
      title: 'Teknologi Mutakhir',
    },
    {
      src: atto8,
      title: 'e-Platform 3.0',
      description: `BYD ATTO 3 dibangun dengan platform baru BYD e-platform 3.0 untuk generasi EV pintar.`,
    },
    {
      src: atto9,
      title: 'Teknologi Baterai Blade Berpaten Ultra-Aman',
      description: `Selama uji penetrasi paku, Baterai Blade tidak mengeluarkan asap atau api setelah ditembus.`,
    },
    {
      src: atto10,
      title: 'BYD ATTO 3',
    },
  ];

  useEffect(() => {
    const currentSections = sections.current;

    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleSections((prev) => {
          const updated = { ...prev };
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              updated[entry.target.dataset.index] = true;
            }
          });
          return updated;
        });
      },
      { threshold: 0.5 },
    );

    currentSections.forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      currentSections.forEach((section) => {
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, []);

  return (
    <Box>
      <PageSeo
        title='BYD Atto 3 BSD Serpong | Harga, Spesifikasi, Test Drive'
        description='Jelajahi BYD Atto 3 di BYD BSD Serpong. Cek harga, spesifikasi, fitur, brosur, promo, dan jadwal test drive bersama Junius.'
        keywords={[
          ...keywordSets.localDealer,
          'harga BYD Atto 3 BSD',
          'BYD Atto 3 Tangerang',
          'spesifikasi BYD Atto 3',
          'test drive BYD Atto 3',
        ]}
        path='/jelajahi-atto'
        image={atto1}
        jsonLd={[
          productJsonLd({
            name: 'BYD Atto 3',
            description:
              'SUV listrik BYD Atto 3 dengan Blade Battery, e-Platform 3.0, dan fitur lengkap untuk mobilitas harian.',
            image: atto1,
            path: '/jelajahi-atto',
            lowPrice: 390000000,
            highPrice: 415000000,
            offerCount: 2,
          }),
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'BYD Atto 3', path: '/jelajahi-atto' },
          ]),
        ]}
      />
      <Typography
        variant='h1'
        component='h1'
        sx={{
          fontSize: { xs: '2rem', sm: '3rem' },
          textAlign: 'center',
          fontWeight: 'bold',
          marginBottom: 4,
          marginTop: 2,
        }}
      >
        Jelajahi BYD Atto
      </Typography>
      {images.map((img, index) => (
        <Box
          key={index}
          ref={(el) => (sections.current[index] = el)}
          data-index={index}
          sx={{
            position: 'relative',
            height: '100vh',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <Box
            component='img'
            src={img.src}
            alt={`Image ${index + 1}`}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: -1,
            }}
          />

          {img.title && (
            <Box
              sx={{
                position: 'absolute',
                top: { xs: '10%', sm: '15%' },
                color: img.src === atto4 || img.src === atto7 ? '#000' : '#fff', // Black for atto4 and atto7
                textAlign: 'center',
                padding: 2,
                transform: visibleSections[index]
                  ? 'translateY(0)'
                  : 'translateY(100%)',
                opacity: visibleSections[index] ? 1 : 0,
                transition: 'all 0.8s ease-in-out',
              }}
            >
              <Typography
                variant='h4'
                fontWeight='bold'
                sx={{
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                }}
              >
                {img.title}
              </Typography>
            </Box>
          )}

          {img.specs && (
            <Box
              sx={{
                position: 'absolute',
                bottom: { xs: '10%', sm: '5%' },
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'center',
                gap: { xs: 2, sm: 4 },
                color: '#fff',
              }}
            >
              {img.specs.map((spec, i) => (
                <React.Fragment key={i}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      variant='h6'
                      fontWeight='bold'
                      sx={{
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                      }}
                    >
                      {spec.value}
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                      }}
                    >
                      {spec.label}
                    </Typography>
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          )}

          {img.description && (
            <Box
              sx={{
                position: 'absolute',
                bottom: { xs: '15%', sm: '10%' },
                color: '#fff',
                textAlign: 'center',
                padding: 2,
                maxWidth: { xs: '90%', sm: '80%' },
                background: 'rgba(0, 0, 0, 0.5)',
                borderRadius: '8px',
                transform: visibleSections[index]
                  ? 'translateY(0)'
                  : 'translateY(100%)',
                opacity: visibleSections[index] ? 1 : 0,
                transition: 'all 0.8s ease-in-out',
              }}
            >
              <Typography
                variant='body1'
                fontWeight='400'
                lineHeight='1.5'
                sx={{
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                }}
              >
                {img.description}
              </Typography>
            </Box>
          )}

          {img.src === atto10 && (
            <Button
              href={brochure}
              download='ATTO3-BROCHURE.pdf'
              variant='outlined'
              sx={{
                position: 'absolute',
                bottom: '10%',
                borderColor: '#fff',
                color: '#fff',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                fontWeight: 600,
                padding: { xs: '8px 16px', sm: '10px 20px' },
                '&:hover': {
                  backgroundColor: '#fff',
                  color: '#000',
                },
              }}
            >
              Download Brosur
            </Button>
          )}
        </Box>
      ))}
      <Box sx={{ padding: { xs: 3, sm: 6 }, backgroundColor: '#f4f4f4' }}>
        <Typography
          variant='h2'
          sx={{
            fontSize: { xs: '1.5rem', sm: '2rem' },
            fontWeight: 600,
            textAlign: 'center',
            mb: 2,
          }}
        >
          Tertarik dengan BYD ATTO 3? 🚙
        </Typography>
        <Typography variant='body1' sx={{ textAlign: 'center', mb: 2 }}>
          Dapatkan <strong>Harga BYD ATTO 3</strong> terbaik hanya di{' '}
          <strong>Dealer Resmi BYD BSD Serpong</strong>. Kami juga menyediakan
          info lengkap tentang <strong>Harga Denza D9</strong>,{' '}
          <strong>Harga Sealion 7</strong>, dan <strong>Harga Atto1</strong>.
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mt: 2,
          }}
        >
          <Button
            href='https://api.whatsapp.com/send?phone=6281286864825&text=Halo,%20saya%20tertarik%20dengan%20BYD%20ATTO%203'
            target='_blank'
            variant='contained'
            color='primary'
          >
            Tanya Harga Sekarang
          </Button>
          <Button href='/jelajahi-sealion' variant='outlined' color='primary'>
            Lihat Harga Sealion 7
          </Button>
          <Button href='/jelajahi-atto1' variant='outlined' color='primary'>
            Lihat Harga Atto1
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default JelajahiAtto;
